import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import cn from 'classnames'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import {
    getCachedAnswer,
    isHydratedAnswerCacheEntry,
    isViewerHydratedAnswerCacheEntry,
} from 'state/cache'
import styles from 'components/answers/Answer/answer.module.scss'
import { ANSWER_HEIGHT } from 'state/PanelState'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import { compensateScrollForMeasurementChange } from 'lib/scrollStability'
import { useScrollResizeCompensation } from 'lib/scrollResizeCompensationContext'
import { answerHeightContentSignature } from 'lib/answerHeightFingerprint'
import {
    MIN_ANSWER_GEOMETRY_HEIGHT,
    rememberRetainedAnswerGeometry,
    resolveRetainedAnswerGeometry,
    resolveInitialAnswerGeometry,
    resolveAnswerContainerStyle,
    resolveAnswerReservedHeight,
} from 'lib/answerGeometryFrame'
import useAnswerChannelRetention from './useAnswerChannelRetention'
import { resolveAnswerInViewAnswer } from './answerInViewAnswer'
import { COLLAPSE_POST_LENGTH, shouldCollapseAnswerText } from './answerCollapseState'

// Keep a non-zero box for inactive rows so IntersectionObserver always has
// measurable geometry. Stream rows use a cheap content-based estimate to avoid
// showing a collapsed quest card while answer hydration/rendering catches up.
const MIN_INACTIVE_PLACEHOLDER_HEIGHT = MIN_ANSWER_GEOMETRY_HEIGHT
const VISIBILITY_RECOVERY_DELAY_MS = 300
const ANSWER_IN_VIEW_ROOT_MARGIN = '1200px 0px 1200px 0px'
const OFFSCREEN_UNMOUNT_DELAY_MS = 1000

const getElementBoxHeight = element => {
    if (!element) return 0
    return Math.max(element.offsetHeight || 0, element.getBoundingClientRect?.()?.height || 0)
}

const getElementExtentHeight = element => {
    if (!element) return 0
    return Math.max(getElementBoxHeight(element), element.scrollHeight || 0)
}

const AnswerInView = ({
    answerId,
    visibleDefault = false,
    children,
    isActive,
    holdMounted = false,
    estimatedWidth = 0,
    isTitle = false,
    isThin = false,
    postLens = false,
    // dm_chat: 'head' | 'cont' rows get their own remembered heights.
    chatRun = null,
    onTextCollapsedChange = null,
    textIsCollapsedOverride = null,
    naturalFlow = false,
    staticGeometry = false,
}) => {
    //console.log('AnswerInView', answerId, visibleDefault, children, isActive)
    const compensateScrollOnResize = useScrollResizeCompensation()
    let answer = resolveAnswerInViewAnswer({
        cachedAnswer: useCachedAnswer(answerId),
        propAnswer: children?.props?.answer,
    })
    const isHydratedAnswer = isHydratedAnswerCacheEntry(answer)
    const needsAnswerHydration = !isViewerHydratedAnswerCacheEntry(answer)
    const initialTextIsCollapsed = () =>
        textIsCollapsedOverride == null ? shouldCollapseAnswerText(answer) : textIsCollapsedOverride
    const initialTextIsCollapsedValue = initialTextIsCollapsed()
    const [isVisible, setIsVisible] = useState(visibleDefault)
    const [hasEnteredView, setHasEnteredView] = useState(visibleDefault)
    const [textIsCollapsed, setTextIsCollapsed] = useState(initialTextIsCollapsedValue)
    const [observerRoot, setObserverRoot] = useState(null)
    const { ensureSubscribed } = useAnswerChannelRetention(answerId, {
        isVisible: naturalFlow || isVisible,
        answer,
    })
    const initialGeometry = resolveInitialAnswerGeometry({
        answerId,
        answer,
        estimatedHeight: isActive ? ANSWER_HEIGHT : 0,
        estimatedWidth,
        isActive,
        isThin,
        isTitle,
        postLens,
        chatRun,
        textIsCollapsed: initialTextIsCollapsedValue,
        useContentEstimate: !isActive && !naturalFlow,
    })
    const initialHeight = initialGeometry.height
    const [height, setHeight] = useState(initialHeight)
    const containerRef = useRef(null)
    const lastMeasuredHeightRef = useRef(initialHeight)
    const lastMeasuredTopRef = useRef(null)
    const heightRef = useRef(initialHeight)
    const offscreenUnmountTimerRef = useRef(null)
    const hasMeasuredHeightRef = useRef(initialGeometry.hasRememberedHeight)
    const pendingOffscreenUnmountMeasurementRef = useRef(null)
    const allowNextMeasuredShrinkRef = useRef(false)
    const textIsCollapsedRef = useRef(initialTextIsCollapsedValue)

    // Don’t override user intent once they expand/collapse manually.
    const userToggledCollapseRef = useRef(false)
    const setTextIsCollapsedWithUserIntent = useCallback(
        val => {
            userToggledCollapseRef.current = true
            allowNextMeasuredShrinkRef.current = true
            const currentValue = textIsCollapsedRef.current
            const nextValue = typeof val === 'function' ? val(currentValue) : val
            // Oversized read-only previews can expose a native "more" control even when the
            // coarse collapse boolean is already false. Still report the explicit expand
            // intent so static stream rows can resize for the full-content render.
            if (nextValue !== currentValue || nextValue === false)
                onTextCollapsedChange?.(nextValue)
            textIsCollapsedRef.current = nextValue
            setTextIsCollapsed(nextValue)
        },
        [onTextCollapsedChange],
    )

    useEffect(() => {
        textIsCollapsedRef.current = textIsCollapsed
    }, [textIsCollapsed])

    const measureRenderedBox = useCallback(
        (root, { includeMountedRowExtent = false, includeScrollPosition = false } = {}) => {
            const row = root || containerRef.current
            const rendered = row?.firstElementChild
            const measuredHeight = Math.max(
                getElementBoxHeight(rendered),
                includeMountedRowExtent ? getElementExtentHeight(row) : 0,
            )
            if (!measuredHeight || measuredHeight <= 0) return null

            const height = Math.max(MIN_INACTIVE_PLACEHOLDER_HEIGHT, Math.ceil(measuredHeight))
            const measurement = {
                height,
                width: Math.ceil(row?.clientWidth || rendered?.clientWidth || 0),
                scrollContainer: null,
                top: null,
            }

            if (!includeScrollPosition) {
                return measurement
            }

            const scrollContainer = observerRoot || row?.closest?.('.scroll-container')
            if (!scrollContainer) return measurement

            const rowRect = row?.getBoundingClientRect?.()
            const scrollRect = scrollContainer?.getBoundingClientRect?.()
            measurement.scrollContainer = scrollContainer
            measurement.top =
                rowRect &&
                scrollRect &&
                Number.isFinite(rowRect.top) &&
                Number.isFinite(scrollRect.top)
                    ? rowRect.top - scrollRect.top
                    : null

            return measurement
        },
        [observerRoot],
    )

    const clearOffscreenUnmountTimer = useCallback(() => {
        if (offscreenUnmountTimerRef.current == null) return
        window.clearTimeout(offscreenUnmountTimerRef.current)
        offscreenUnmountTimerRef.current = null
    }, [])

    const setMeasuredHeight = useCallback(
        (
            nextMeasurement,
            { compensate = false, preserveHeight = false, allowShrink = false } = {},
        ) => {
            const measuredHeight =
                typeof nextMeasurement === 'number' ? nextMeasurement : nextMeasurement?.height
            if ((!measuredHeight || measuredHeight <= 0) && !preserveHeight) return

            const nextHeight = resolveRetainedAnswerGeometry({
                measuredHeight,
                previousHeight: heightRef.current || 0,
                previousMeasuredHeight: lastMeasuredHeightRef.current || 0,
                hasMeasuredHeight: hasMeasuredHeightRef.current,
                preserveHeight,
                allowShrink,
                isThin,
                minHeight: MIN_INACTIVE_PLACEHOLDER_HEIGHT,
            })
            if (!nextHeight || nextHeight <= 0) return

            const previousHeight = lastMeasuredHeightRef.current
            const previousTop = lastMeasuredTopRef.current
            const nextTop = typeof nextMeasurement === 'number' ? null : nextMeasurement?.top
            const scrollContainer =
                typeof nextMeasurement === 'number' ? null : nextMeasurement?.scrollContainer
            const measuredWidth = typeof nextMeasurement === 'number' ? 0 : nextMeasurement?.width
            const { applied: appliedScrollCompensation, delta } = compensate
                ? compensateScrollForMeasurementChange({
                      scrollContainer,
                      previousHeight,
                      previousTop,
                      nextHeight,
                      nextTop,
                  })
                : { applied: false, delta: nextHeight - previousHeight }

            lastMeasuredHeightRef.current = nextHeight
            lastMeasuredTopRef.current =
                Number.isFinite(nextTop) && appliedScrollCompensation ? nextTop - delta : nextTop
            if (typeof nextMeasurement !== 'number' && measuredWidth > 0) {
                hasMeasuredHeightRef.current = true
            }
            rememberRetainedAnswerGeometry({
                answerId,
                height: nextHeight,
                width: measuredWidth,
                isActive,
                isThin,
                isTitle,
                postLens,
                chatRun,
                textIsCollapsed,
                contentSignature: answerHeightContentSignature(answer),
            })

            if (allowShrink) allowNextMeasuredShrinkRef.current = false

            if (Math.abs(heightRef.current - nextHeight) <= 1) return

            heightRef.current = nextHeight
            setHeight(nextHeight)
        },
        [answer, answerId, isActive, isThin, isTitle, postLens, chatRun, textIsCollapsed],
    )

    const preserveRenderedHeight = useCallback(
        ({ includeScrollPosition = false } = {}) => {
            const nextMeasurement = measureRenderedBox(null, {
                includeMountedRowExtent: true,
                includeScrollPosition,
            })
            if (nextMeasurement != null)
                setMeasuredHeight(nextMeasurement, { preserveHeight: true })
            return nextMeasurement
        },
        [measureRenderedBox, setMeasuredHeight],
    )

    const scheduleOffscreenUnmount = useCallback(() => {
        if (holdMounted || naturalFlow) return
        clearOffscreenUnmountTimer()
        offscreenUnmountTimerRef.current = window.setTimeout(() => {
            pendingOffscreenUnmountMeasurementRef.current = preserveRenderedHeight({
                includeScrollPosition: true,
            })
            setHasEnteredView(false)
            offscreenUnmountTimerRef.current = null
        }, OFFSCREEN_UNMOUNT_DELAY_MS)
    }, [clearOffscreenUnmountTimer, holdMounted, naturalFlow, preserveRenderedHeight])

    const updateRenderedHeight = useCallback(
        options => {
            const nextMeasurement = measureRenderedBox(null, {
                includeMountedRowExtent: Boolean(options?.includeMountedRowExtent),
                includeScrollPosition: Boolean(options?.compensate),
            })
            if (nextMeasurement != null) setMeasuredHeight(nextMeasurement, options)
        },
        [measureRenderedBox, setMeasuredHeight],
    )

    const resolveObserverRoot = useCallback(
        node => node?.closest?.('.scroll-container') || null,
        [],
    )

    const syncObserverRoot = useCallback(
        (node = containerRef.current) => {
            const nextRoot = resolveObserverRoot(node)
            setObserverRoot(currentRoot => (currentRoot === nextRoot ? currentRoot : nextRoot))
        },
        [resolveObserverRoot],
    )

    // If the answer loads/updates after mount, recompute initial collapsed state
    // (until the user manually toggles it).
    useEffect(() => {
        if (textIsCollapsedOverride != null) {
            setTextIsCollapsed(textIsCollapsedOverride)
            return
        }
        if (userToggledCollapseRef.current) return
        setTextIsCollapsed(shouldCollapseAnswerText(answer))
    }, [answerId, answer, textIsCollapsedOverride])

    useEffect(() => {
        if (naturalFlow) return
        if (isActive || (hasEnteredView && isHydratedAnswer)) return

        const nextHeight = resolveAnswerReservedHeight({
            answer,
            estimatedWidth,
            isThin,
            isTitle,
            postLens,
            textIsCollapsed,
        })
        setMeasuredHeight(nextHeight, { preserveHeight: hasMeasuredHeightRef.current })
    }, [
        answer,
        estimatedWidth,
        hasEnteredView,
        isActive,
        isHydratedAnswer,
        isThin,
        isTitle,
        naturalFlow,
        postLens,
        setMeasuredHeight,
        textIsCollapsed,
    ])

    //const scrollContainer = panel.getPanelRef()?.querySelector('.scroll-container')

    const handleInViewChange = useCallback(
        (inView, entry) => {
            // Treat any intersection as "in view" so very tall elements
            // (with low intersection ratios) don't get unloaded.
            const isIntersecting = entry?.isIntersecting ?? inView
            if (isIntersecting) {
                clearOffscreenUnmountTimer()
                setHasEnteredView(true)
                setIsVisible(true)
                if (needsAnswerHydration) queueAnswerHydration(answerId, { lane: 'visible' })
                ensureSubscribed()
            } else {
                if (!naturalFlow) {
                    const renderedMeasurement = measureRenderedBox(entry?.target, {
                        includeMountedRowExtent: true,
                        includeScrollPosition: true,
                    })
                    if (renderedMeasurement != null) {
                        setMeasuredHeight(renderedMeasurement, { preserveHeight: true })
                    } else if (entry?.target?.clientHeight > MIN_INACTIVE_PLACEHOLDER_HEIGHT) {
                        setMeasuredHeight(entry.target.clientHeight, { preserveHeight: true })
                    } else {
                        setMeasuredHeight(heightRef.current || lastMeasuredHeightRef.current, {
                            preserveHeight: true,
                        })
                    }
                }
                setIsVisible(false)
                scheduleOffscreenUnmount()
            }
        },
        [
            answerId,
            clearOffscreenUnmountTimer,
            ensureSubscribed,
            isHydratedAnswer,
            measureRenderedBox,
            naturalFlow,
            needsAnswerHydration,
            scheduleOffscreenUnmount,
            setMeasuredHeight,
        ],
    )

    const { ref } = useInView({
        // Use the stream scroller as root when available; any visible pixel counts as in view.
        root: observerRoot,
        rootMargin: ANSWER_IN_VIEW_ROOT_MARGIN,
        threshold: 0,
        onChange: handleInViewChange,
        skip: naturalFlow,
    })

    const setRefs = useCallback(
        node => {
            containerRef.current = node
            if (node && !naturalFlow) syncObserverRoot(node)
            ref(node)
        },
        [naturalFlow, ref, syncObserverRoot],
    )

    useLayoutEffect(() => {
        if (naturalFlow) return
        syncObserverRoot()
    }, [naturalFlow, syncObserverRoot])

    useEffect(() => {
        if (naturalFlow) return
        if (holdMounted || isVisible) {
            clearOffscreenUnmountTimer()
            return
        }

        if (hasEnteredView) scheduleOffscreenUnmount()
    }, [
        clearOffscreenUnmountTimer,
        hasEnteredView,
        holdMounted,
        isVisible,
        naturalFlow,
        scheduleOffscreenUnmount,
    ])

    useEffect(() => {
        if (naturalFlow || isVisible) return

        // Safety net for rare observer stalls: if this row is already on-screen
        // and still hidden after a short delay, mount it once and subscribe.
        const timer = window.setTimeout(() => {
            const el = containerRef.current
            if (!el || isVisible) return

            const rect = el.getBoundingClientRect()
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
            const isInViewport = rect.bottom >= 0 && rect.top <= viewportHeight

            if (isInViewport) {
                clearOffscreenUnmountTimer()
                setHasEnteredView(true)
                setIsVisible(true)
                if (needsAnswerHydration) queueAnswerHydration(answerId, { lane: 'visible' })
                ensureSubscribed()
            }
        }, VISIBILITY_RECOVERY_DELAY_MS)

        return () => {
            window.clearTimeout(timer)
        }
    }, [
        answerId,
        clearOffscreenUnmountTimer,
        ensureSubscribed,
        isHydratedAnswer,
        isVisible,
        naturalFlow,
        needsAnswerHydration,
    ])

    useEffect(() => {
        if (!answerId || !needsAnswerHydration) return
        if (!naturalFlow && !isVisible && !visibleDefault) return
        queueAnswerHydration(answerId, { lane: 'visible' })
    }, [answerId, isVisible, naturalFlow, needsAnswerHydration, visibleDefault])

    const shouldRender = (naturalFlow || hasEnteredView || holdMounted) && isHydratedAnswer

    useLayoutEffect(() => {
        if (naturalFlow) return
        // ResizeObserver protects mounted content growth; this captures the
        // mounted-to-placeholder swap itself.
        if (shouldRender) {
            pendingOffscreenUnmountMeasurementRef.current = null
            return
        }

        const previousMeasurement = pendingOffscreenUnmountMeasurementRef.current
        if (previousMeasurement == null) return

        pendingOffscreenUnmountMeasurementRef.current = null
        const nextMeasurement = measureRenderedBox(null, {
            includeMountedRowExtent: true,
            includeScrollPosition: true,
        })
        if (nextMeasurement == null) return

        setMeasuredHeight(
            {
                ...nextMeasurement,
                height: Math.max(previousMeasurement.height, nextMeasurement.height),
                scrollContainer:
                    nextMeasurement.scrollContainer || previousMeasurement.scrollContainer,
            },
            { compensate: compensateScrollOnResize, preserveHeight: true },
        )
    }, [compensateScrollOnResize, measureRenderedBox, naturalFlow, setMeasuredHeight, shouldRender])

    useEffect(() => {
        if (naturalFlow || !shouldRender) return

        const rendered = containerRef.current?.firstElementChild
        if (!rendered) return

        const updateOptions = {
            compensate: compensateScrollOnResize,
            includeMountedRowExtent: !isVisible,
            preserveHeight: !isVisible,
            allowShrink: allowNextMeasuredShrinkRef.current,
        }

        if (typeof ResizeObserver === 'undefined') {
            const frameId = window.requestAnimationFrame(() => updateRenderedHeight(updateOptions))
            return () => window.cancelAnimationFrame(frameId)
        }

        let frameId = null
        const observer = new ResizeObserver(() => {
            if (frameId != null) window.cancelAnimationFrame(frameId)
            frameId = window.requestAnimationFrame(() => updateRenderedHeight(updateOptions))
        })
        observer.observe(rendered)
        updateRenderedHeight(updateOptions)

        return () => {
            if (frameId != null) window.cancelAnimationFrame(frameId)
            observer.disconnect()
        }
    }, [
        answerId,
        compensateScrollOnResize,
        isVisible,
        naturalFlow,
        shouldRender,
        textIsCollapsed,
        updateRenderedHeight,
    ])

    useEffect(() => {
        return () => clearOffscreenUnmountTimer()
    }, [clearOffscreenUnmountTimer])

    const inViewCallback = useCallback(
        options => {
            // A deliberate viewer action (a payload flipped to the shorter carousel) arms the
            // same shrink latch the text collapse toggle uses for the observer-driven measure.
            if (options?.userIntent) allowNextMeasuredShrinkRef.current = true
            if (!naturalFlow) {
                const allowShrink = Boolean(options?.allowShrink)
                updateRenderedHeight(
                    allowShrink
                        ? {
                              compensate: compensateScrollOnResize,
                              allowShrink: true,
                          }
                        : undefined,
                )
            }
            answer = getCachedAnswer(answerId)
        },
        [answerId, compensateScrollOnResize, naturalFlow, updateRenderedHeight],
    )

    const intrinsicHeight = Math.max(MIN_INACTIVE_PLACEHOLDER_HEIGHT, height)
    const containerStyle = naturalFlow
        ? undefined
        : resolveAnswerContainerStyle({
              intrinsicHeight,
              shouldRender,
          })

    return (
        <div
            ref={setRefs}
            className={cn(
                styles.answerInView,
                !shouldRender && styles.answerInViewPlaceholder,
                !shouldRender && isTitle && styles.answerInViewTitlePlaceholder,
                `answer-in-view-${answerId}`,
            )}
            style={containerStyle}
            data-natural-flow-geometry-pending={naturalFlow && !shouldRender ? 'true' : undefined}
            data-natural-feed-geometry-pending={naturalFlow && !shouldRender ? 'true' : undefined}
        >
            {shouldRender &&
                React.cloneElement(children, {
                    key: `answer-${answerId}`,
                    answer,
                    inViewCallback,
                    textIsCollapsed,
                    setTextIsCollapsed: setTextIsCollapsedWithUserIntent,
                    COLLAPSE_POST_LENGTH,
                    estimatedWidth,
                    staticGeometry,
                })}
        </div>
    )
}

export default AnswerInView
